import { Footer } from '@components/footer/models';
import { LabeledLink, MenuCategory, MenuCategoryWithSubItems, SvgIconLink } from '@shared/models';
import { RegionAvailable } from '@shared/services/language/registry';
import { JsonLD } from '@shared/services/seo/models';
import { envConfiguredHostname, normalizeUrlPath } from '@shared/utils';

/**
 * Constructs a JSON-LD object for a footer section, in compliance with schema.org standards.
 *
 * @param {string} name - The name of the footer, typically identifying the site or section.
 * @param {Footer} footer - The footer data containing social networks and menu categories.
 * @returns {JsonLD} - A JSON-LD object representing the footer, with relevant metadata for SEO.
 *
 * The returned JSON-LD includes two primary sections:
 *  - An `Organization` type that provides information such as the site's name, URL, and associated social network links.
 *  - A `SiteNavigationElement` type that structures navigation, with categories and subitems as web pages.
 *
 * @see FooterComponent, its caller.
 */
export const buildFooterJsonLd = (name: string, footer: Footer, region: RegionAvailable): JsonLD => {
  const base = envConfiguredHostname(region);
  const toAbs = (url: string | undefined | null): string | null => {
    if (!url || url === '/') return null;
    return /^https?:\/\//i.test(url) ? url : normalizeUrlPath(`${base}${url}`);
  };
  const isNonNull = <T>(v: T | null): v is T => v !== null;

  const sameAs: string[] = (footer?.items?.socialNetworks?.data?.pictos ?? [])
    .map((p: SvgIconLink) => p?.url)
    .map(toAbs) // garde les absolues / résout les relatives
    .filter(isNonNull);

  const hasPart = (footer?.menu?.categories ?? [])
    .map((c: unknown) => {
      const pages = ((c as MenuCategoryWithSubItems)?.subItems ?? [])
        .map((s: LabeledLink) => {
          const url = toAbs(s?.url);
          if (!url) return null;
          return { '@type': 'WebPage', name: s?.label ?? '', url };
        })
        .filter(isNonNull);

      if (!(c as MenuCategory)?.label || pages.length === 0) return null;
      return { '@type': 'SiteNavigationElement', name: (c as MenuCategory).label, hasPart: pages };
    })
    .filter(isNonNull);

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: "SoCoo'c",
        url: normalizeUrlPath(base),
        sameAs,
      },
      {
        '@type': 'SiteNavigationElement',
        name,
        hasPart,
      },
    ],
  } as unknown as JsonLD;
};
